<template>
    <tr class="cursor-pointer" @click="editProject">
        <TableCell truncate>
            <div class="flex items-center overflow-hidden">
                <CardHoverLink
                    :icon="icon"
                    :link="project.previewUrl"
                    class="hidden sm:block mr-3"
                />
                <div class="overflow-hidden">
                    <p class="font-medium truncate">
                        {{ project.name }}
                    </p>
                    <p class="ct-text-3">
                        {{ badgeText }}
                    </p>
                </div>
            </div>
        </TableCell>
        <TableCell text-right sm-hidden>
            <p class="ct-text-3">
                {{ createdTime }}
            </p>
        </TableCell>
        <TableCell text-right sm-hidden>
            <p class="ct-text-3">{{ updatedTime }}</p>

            <BadgeStatusSm
                v-if="publishedTime"
                type="success"
                class="-mr-1.5 mt-0.5 flex items-center space-x-1"
            >
                <RocketIcon class="h-3 w-3" />
                <span>{{ publishedTime }}</span>
            </BadgeStatusSm>
        </TableCell>
        <TableCell text-right>
            <BadgeStatusSm :type="isPublished(project) ? 'success' : 'neutral'"
                >{{ accessLabel(project) }}
            </BadgeStatusSm>
        </TableCell>
        <TableCell text-right v-if="activeApp === 'player'">
            <div class="w-full flex justify-end items-center" v-if="project?.attributes?.statsUrl">
                <ButtonTertiaryMd @click.stop="goTo(project?.attributes?.statsUrl)">
                    <span>Show</span>
                    <GraphBarIncreaseIcon class="-mt-1" />
                </ButtonTertiaryMd>
            </div>
        </TableCell>
        <TableCell sm-hidden>
            <cProjectDropdown
                @edit="editProject"
                @clone="cloneProject"
                @rename="renameProject"
                @delete="removeProject"
                @copyLink="copyLink"
            />
        </TableCell>
    </tr>
</template>

<script>
import { projectsService } from "@/shared/projects/projects.service";

import { copy } from "@/shared/utils/string";
import { mapGetters, mapMutations, mapState } from "vuex";
import { i18n } from "@/i18n";
import { TableCell } from "@easy/ui/tables";
import { CardHoverLink } from "@easy/ui/cards";
import { BadgeStatusSm } from "@easy/ui/badges";
import { GraphBarIncreaseIcon, RocketIcon } from "@easy/ui/icons";
import { ButtonTertiaryMd } from "@easy/ui/buttons";
import moment from "moment";
import cProjectDropdown from "./cProjectDropdown.vue";

export default {
    name: "cProjectRow",
    components: {
        TableCell,
        CardHoverLink,
        BadgeStatusSm,
        cProjectDropdown,
        GraphBarIncreaseIcon,
        ButtonTertiaryMd,
        RocketIcon,
    },
    props: {
        project: {
            type: Object,
            required: true,
        },
        icon: {
            type: Object,
        },
        badgeText: String,
    },
    computed: {
        ...mapState(["activeApp"]),
        ...mapGetters(["isPublished", "accessLabel"]),
        createdTime() {
            return moment(this.project.createdAt).locale(i18n.global.locale.value).fromNow();
        },
        updatedTime() {
            return moment(this.project.updatedAt).locale(i18n.global.locale.value).fromNow();
        },
        publishedTime() {
            if (!this.project.publishedAt || !this.isPublished(this.project)) {
                return "";
            }
            return moment(this.project.publishedAt).locale(i18n.global.locale.value).fromNow();
        },
    },
    methods: {
        ...mapMutations(["showModal", "showAlert"]),
        editProject() {
            this.goTo(this.project.url);
        },
        goTo(url) {
            window.location.href = url;
        },
        renameProject() {
            const modalData = {
                type: "input",
                input: this.project.name,
                title: this.$t("shared.list.rename_project"),
                cancelBtn: this.$t("shared.cancel"),
                actionBtn: this.$t("shared.save"),

                callback: () => {
                    this.rename(modalData.input);
                },
            };
            this.showModal(modalData);
        },
        async rename(name) {
            await projectsService.rename({
                tool: this.project.tool,
                uuid: this.project.uuid,
                name,
            });
            window.location.reload();
        },
        cloneProject() {
            const newName = `${this.project.name} (1)`;

            const modalData = {
                type: "input",
                input: newName,
                title: this.$t("shared.list.name_project"),
                cancelBtn: this.$t("shared.cancel"),
                actionBtn: this.$t("shared.list.duplicate"),

                callback: () => {
                    this.clone(modalData.input);
                },
            };
            this.showModal(modalData);
        },
        async clone(name) {
            await projectsService.clone({
                tool: this.project.tool,
                uuid: this.project.uuid,
                name,
            });
            window.location.reload();
        },
        removeProject() {
            this.showModal({
                type: "confirm",
                title: this.$t("shared.list.delete_project_title"),
                desc: this.$t("shared.list.delete_project_desc"),
                cancelBtn: this.$t("shared.cancel"),
                actionBtn: this.$t("shared.delete"),
                callback: this.remove,
            });
        },
        async remove() {
            await projectsService.remove({
                tool: this.project.tool,
                uuid: this.project.uuid,
            });
            window.location.reload();
        },
        copyLink() {
            const origin = window.location.origin;
            copy(`${origin}${this.project.url}`);
            this.showAlert({
                type: "success",
                message: this.$t("shared.link_copied"),
            });
        },
    },
};
</script>
